// Lining a model up along the steps that carry a tag: the "Hierarchic tag path" layout. The path runs through the
// tagged nodes and whatever joins them (such as the information passed from one tagged action to the next), so these
// work out which nodes those are and how much each edge counts towards the path.

// the tag a model is lined up by
export interface LayoutTag {
    groupKey: string
    tagKey: string
}

// how much more an edge into a tagged node counts than any other, so the path takes in as many tagged nodes as it can
export const TAGGED_EDGE_COST = 10

export const hasTag = (properties: { tagKeys?: { [groupKey: string]: string } } | undefined, tag: LayoutTag) =>
    !!properties?.tagKeys && properties.tagKeys[tag.groupKey] === tag.tagKey

// the nodes the path can use: the tagged ones, and those both downstream of a tagged node and upstream of one
export const nodesBetweenTagged = <T>(nodes: T[], successors: (node: T) => T[], predecessors: (node: T) => T[], tagged: (node: T) => boolean): Set<T> => {
    const start = nodes.filter(tagged)
    const reach = (next: (node: T) => T[]) => {
        const seen = new Set<T>(start)
        const queue = [...start]
        while (queue.length) {
            for (const n of next(queue.shift()!)) {
                if (!seen.has(n)) {
                    seen.add(n)
                    queue.push(n)
                }
            }
        }
        return seen
    }
    const downstream = reach(successors)
    const upstream = reach(predecessors)
    return new Set(nodes.filter(n => tagged(n) || (downstream.has(n) && upstream.has(n))))
}
